import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { APIService } from "src/app/api/api.service";

interface NodeGroupTag {
  key: string;
  value: string;
}

interface NodeGroupForm {
  launch_justification: string;
  nodegroup_name: string;
  node_role_arn: string | null;
  subnet_ids: string[];
  instance_types: string[];
  ami_type: string | null;
  scaling_min: number;
  scaling_max: number;
  scaling_desired: number;
  disk_size: number;
  capacity_type: string;
  tags: NodeGroupTag[];
}

interface AmiOption {
  ami_type: string;
  label?: string;
}

@Component({
  selector: "app-nodegroup-modal",
  templateUrl: "./nodegroup-modal.component.html",
  styleUrls: ["../eks-requests.styles.css"],
})
export class NodegroupModalComponent implements OnChanges {
  @Input() visible = false;
  @Input() nodeGroupContext: any = null;
  @Input() accountId = "";
  @Input() region = "";
  @Input() apiBaseUrl = "";
  @Input() urlPrefix = "";
  @Output() close = new EventEmitter<void>();
  @Output() created = new EventEmitter<void>();

  nodeGroupOptions: any = { nodeRoles: [], instanceTypes: [], amiOptions: [] as AmiOption[] };
  nodeGroupLoading: any = { requestDetails: false, nodeRoles: false, amiOptions: false, pricing: false, submit: false };
  nodeGroupForm: NodeGroupForm = this.getDefaultNodeGroupForm();
  nodeGroupPricing: any = null;
  pricingError = "";
  showPricingStep = false;
  formError = "";
  amiWarning = "";
  createSuccessMessage = "";
  createErrorMessage = "";

  constructor(private apiServer: APIService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes["visible"] || changes["nodeGroupContext"]) && this.visible && this.nodeGroupContext) {
      this.initializeModal();
    }
  }

  private headers() { return { Authorization: localStorage.getItem("t") }; }
  private async get(url: string) {
    try {
      const r = await this.apiServer.getDataPromis(url, {}, this.headers());
      return typeof r == "string" ? null : r;
    } catch {
      return null;
    }
  }

  private getDefaultNodeGroupForm(): NodeGroupForm {
    return {
      nodegroup_name: "",
      launch_justification: "",
      node_role_arn: null,
      subnet_ids: [],
      instance_types: [],
      ami_type: null,
      scaling_min: 1,
      scaling_max: 10,
      scaling_desired: 2,
      disk_size: 20,
      capacity_type: "ON_DEMAND",
      tags: [{ key: "", value: "" }],
    };
  }

  private async initializeModal() {
    let row = this.nodeGroupContext;
    this.nodeGroupForm = this.getDefaultNodeGroupForm();
    this.nodeGroupPricing = null;
    this.pricingError = "";
    this.showPricingStep = false;
    this.formError = "";
    this.amiWarning = "";
    this.createSuccessMessage = "";
    this.createErrorMessage = "";
    const details = await this.loadRequestDetails(row);
    row = details || row;
    this.nodeGroupContext = row;
    this.nodeGroupForm.subnet_ids = Array.isArray(row?.subnet_ids) ? [...row.subnet_ids] : [];
    await Promise.all([this.loadNodeRoles(row), this.loadInstanceTypes(row), this.loadAmiOptions(row)]);
  }

  async loadRequestDetails(row: any): Promise<any> {
    this.nodeGroupLoading.requestDetails = true;
    try {
      const requestId = (row?.request_id || "").toString().trim();
      const resolvedRegion = row?.region || this.region;
      if (!requestId || !this.accountId || !resolvedRegion) return row;
      const path = `apis/eks_launch/api/eks/request/${encodeURIComponent(requestId)}?aws_account_id=${encodeURIComponent(this.accountId)}&region=${encodeURIComponent(resolvedRegion)}`;
      const url = `${this.apiBaseUrl}/${this.urlPrefix}/${path}`;
      const r = await this.get(url);
      const payload = r?.request_payload || r?.data?.request_payload || r?.payload || r || {};
      const subnetIds = Array.isArray(payload?.subnet_ids) ? payload.subnet_ids : (Array.isArray(payload?.subnets) ? payload.subnets : []);
      const kubernetesVersion = (payload?.kubernetes_version || "").toString().trim();
      return {
        ...row,
        subnet_ids: subnetIds,
        kubernetes_version: kubernetesVersion || row?.kubernetes_version || "",
      };
    } finally {
      this.nodeGroupLoading.requestDetails = false;
    }
  }

  requestClose() {
    if (this.nodeGroupLoading.submit) return;
    this.close.emit();
  }

  private normalize(value: any): string {
    const v = (value ?? "").toString().trim();
    return !v || v.toLowerCase() === "null" || v.toLowerCase() === "undefined" ? "" : v;
  }

  private getRequesterId(): string {
    return this.normalize(localStorage.getItem("eId"));
  }

  private getRequesterEmail(): string {
    return (
      this.normalize(localStorage.getItem("email")) ||
      this.normalize(localStorage.getItem("userEmail")) ||
      this.normalize(localStorage.getItem("eId"))
    );
  }

  private getJiraProjectKey(): string {
    return (
      this.normalize(localStorage.getItem("swayam_jira_project_key")) ||
      this.normalize(localStorage.getItem("jira_project_key"))
    );
  }

  private getJiraOrganizationKey(): string {
    return (
      this.normalize(localStorage.getItem("swayam_jira_org_name")) ||
      this.normalize(localStorage.getItem("jira_organization_key")) ||
      this.normalize(localStorage.getItem("swayam_jira_org_key"))
    );
  }

  async loadNodeRoles(row: any) {
    this.nodeGroupLoading.nodeRoles = true;
    const account = this.accountId;
    const resolvedRegion = row?.region || this.region;
    const path = `apis/eks-master-data/api/eks/node-roles?account_id=${encodeURIComponent(account)}&region=${encodeURIComponent(resolvedRegion)}&service_name=ec2.amazonaws.com`;
    const url = `${this.apiBaseUrl}/${this.urlPrefix}/${path}`;
    const r = await this.get(url);
    this.nodeGroupOptions.nodeRoles = r?.node_roles || [];
    this.nodeGroupLoading.nodeRoles = false;
  }

  async loadInstanceTypes(row: any) {
    const account = this.accountId;
    const resolvedRegion = row?.region || this.region;
    const path = `apis/ec2-get-master-data/ec2/instance-types?account_id=${encodeURIComponent(account)}&region=${encodeURIComponent(resolvedRegion)}`;
    const url = `${this.apiBaseUrl}/${this.urlPrefix}/${path}`;
    const r = await this.get(url);
    this.nodeGroupOptions.instanceTypes = r?.items || [];
  }

  async loadAmiOptions(row: any) {
    this.nodeGroupLoading.amiOptions = true;
    const resolvedRegion = row?.region || this.region;
    const kubernetesVersion = (
      row?.kubernetes_version ||
      row?.request_payload?.kubernetes_version ||
      row?.request_data?.kubernetes_version ||
      row?.payload?.kubernetes_version ||
      ""
    ).toString().trim();
    if (!resolvedRegion || !kubernetesVersion) {
      this.nodeGroupOptions.amiOptions = [];
      if (!kubernetesVersion) {
        this.amiWarning = "Kubernetes version not found for this request, so AMI options could not be loaded.";
      }
      this.nodeGroupLoading.amiOptions = false;
      return;
    }
    const path = `apis/eks_launch/api/eks/ami-options?region=${encodeURIComponent(resolvedRegion)}&kubernetes_version=${encodeURIComponent(kubernetesVersion)}`;
    const url = `${this.apiBaseUrl}/${this.urlPrefix}/${path}`;
    const r = await this.get(url);
    this.nodeGroupOptions.amiOptions = Array.isArray(r?.options) ? r.options : [];
    this.amiWarning = this.nodeGroupOptions.amiOptions.length ? "" : "No AMI options found for selected cluster version/region.";
    if (this.nodeGroupOptions.amiOptions.length > 0 && this.nodeGroupForm.ami_type) {
      const selectedExists = this.nodeGroupOptions.amiOptions.some((o: AmiOption) => o?.ami_type === this.nodeGroupForm.ami_type);
      if (!selectedExists) this.nodeGroupForm.ami_type = null;
    }
    this.nodeGroupLoading.amiOptions = false;
  }

  private buildRequestBody() {
    const row = this.nodeGroupContext;
    const subnetIds = this.nodeGroupForm.subnet_ids || [];
    const instanceTypes = this.nodeGroupForm.instance_types || [];
    return {
      requester_id: this.getRequesterId(),
      requester_email: this.getRequesterEmail(),
      launch_justification: this.nodeGroupForm.launch_justification.trim(),
      jira_project_key: this.getJiraProjectKey(),
      jira_organization_key: this.getJiraOrganizationKey(),
      request_payload: {
        aws_account_id: this.accountId,
        region: row.region || this.region,
        cluster_name: row.cluster_name,
        nodegroup_name: this.nodeGroupForm.nodegroup_name.trim(),
        node_role_arn: this.nodeGroupForm.node_role_arn,
        subnet_ids: subnetIds,
        instance_types: instanceTypes,
        ami_type: this.nodeGroupForm.ami_type,
        scaling_min: Number(this.nodeGroupForm.scaling_min),
        scaling_max: Number(this.nodeGroupForm.scaling_max),
        scaling_desired: Number(this.nodeGroupForm.scaling_desired),
        disk_size: Number(this.nodeGroupForm.disk_size),
        capacity_type: this.nodeGroupForm.capacity_type,
        tags: (this.nodeGroupForm.tags || [])
          .map((t: any) => ({ key: (t.key || "").trim(), value: (t.value || "").trim() }))
          .filter((t: any) => t.key && t.value),
      },
    };
  }

  async submitNodeGroupRequest() {
    if (!this.nodeGroupContext || this.nodeGroupLoading.submit) return;
    this.formError = "";
    this.createSuccessMessage = "";
    this.createErrorMessage = "";
    const subnetIds = this.nodeGroupForm.subnet_ids || [];
    const instanceTypes = this.nodeGroupForm.instance_types || [];
    if (!this.nodeGroupForm.launch_justification.trim() || !this.nodeGroupForm.nodegroup_name.trim() || !this.nodeGroupForm.node_role_arn || subnetIds.length === 0 || instanceTypes.length === 0 || !this.nodeGroupForm.ami_type) {
      this.formError = "Please fill required node group fields.";
      return;
    }
    this.nodeGroupLoading.submit = true;
    try {
      const url = `${this.apiBaseUrl}/${this.urlPrefix}/apis/eks_launch/api/eks/nodegroup/save-request`;
      const result: any = await this.apiServer.postDataPromis(url, this.buildRequestBody(), this.headers());
      if (!result || typeof result == "string" || result.detail || result.error || (result.status !== undefined && result.status !== "1" && result.status !== 1) || (result.s !== undefined && result.s !== "1" && result.s !== 1)) {
        let errorMsg = result?.message || result?.msg || (Array.isArray(result?.detail) ? result.detail[0]?.msg : null) || result?.error || "Failed to submit node group request.";
        if (typeof errorMsg === "string" && errorMsg.startsWith("Value error, ")) {
          errorMsg = errorMsg.replace("Value error, ", "");
        }
        throw new Error(errorMsg);
      }
      this.createSuccessMessage = result.message || "Node group request submitted successfully.";
      this.apiServer.notifier.alert("Success", "", this.createSuccessMessage, "success", 4000);
      this.created.emit();
    } catch (err: any) {
      this.createErrorMessage = err?.message || "Failed to submit node group request.";
    } finally {
      this.nodeGroupLoading.submit = false;
    }
  }

  async goToPricing() {
    if (!this.nodeGroupContext || this.nodeGroupLoading.pricing) return;
    this.formError = "";
    const instanceTypes = this.nodeGroupForm.instance_types || [];
    const subnetIds = this.nodeGroupForm.subnet_ids || [];
    if (!this.nodeGroupForm.launch_justification.trim() || !this.nodeGroupForm.nodegroup_name.trim() || !this.nodeGroupForm.node_role_arn || subnetIds.length === 0 || !this.nodeGroupForm.ami_type || instanceTypes.length === 0) {
      this.formError = "Please fill required node group fields before continuing to pricing.";
      return;
    }

    const row = this.nodeGroupContext;
    const payload = {
      region: row?.region || this.region,
      instance_type: instanceTypes[0],
      ami_type: this.nodeGroupForm.ami_type,
      desired_count: Number(this.nodeGroupForm.scaling_desired),
      capacity_type: "ON_DEMAND",
    };

    this.nodeGroupLoading.pricing = true;
    this.nodeGroupPricing = null;
    this.pricingError = "";
    this.createSuccessMessage = "";
    this.createErrorMessage = "";
    try {
      const url = `${this.apiBaseUrl}/${this.urlPrefix}/apis/eks_launch/api/eks/nodegroup-pricing`;
      const r: any = await this.apiServer.postDataPromis(url, payload, this.headers());
      if (!r || typeof r == "string") throw new Error("Invalid response");
      this.nodeGroupPricing = r;
      this.showPricingStep = true;
    } catch {
      this.pricingError = "Unable to fetch nodegroup pricing right now.";
    } finally {
      this.nodeGroupLoading.pricing = false;
    }
  }

  backToForm() {
    if (this.nodeGroupLoading.pricing || this.nodeGroupLoading.submit) return;
    this.showPricingStep = false;
    this.formError = "";
    this.createSuccessMessage = "";
    this.createErrorMessage = "";
  }

  addNodeGroupTag() { this.nodeGroupForm.tags.push({ key: "", value: "" }); }

  removeNodeGroupTag(index: number) {
    if (this.nodeGroupForm.tags.length === 1) {
      this.nodeGroupForm.tags[0] = { key: "", value: "" };
      return;
    }
    this.nodeGroupForm.tags.splice(index, 1);
  }
}
